"use client";

import { c as _c } from "react/compiler-runtime";
import { Fragment, useMemo, useState } from "react";
import { FiAlertTriangle, FiBarChart2, FiChevronDown, FiChevronRight, FiChevronUp, FiEdit2, FiGrid, FiList, FiPlus } from "react-icons/fi";
import { IoBodyOutline } from "react-icons/io5";
import { TbCalculator, TbTarget } from "react-icons/tb";
import { goalElements } from "@/components/measurement-charts/chart-annotation-elements";
import FieldTogglePills from "@/components/measurement-charts/field-toggle-pills";
import MeasurementPanelChart, { type ChartField, type MeasurementPanelChartDatum } from "@/components/measurement-charts/measurement-panel-chart";
import ScoreDonutChart from "@/components/measurement-charts/score-donut-chart";
import TrendChartsSection from "@/components/measurement-charts/trend-charts-section";
import Button from "@/components/ui/button";
import Card from "@/components/ui/card";
import ChartWrapper from "@/components/ui/charts/chart-wrapper";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownSettingsButton } from "@/components/ui/dropdown";
import { Table, TableBody, TableCell, TableFoot, TableHead, TableHeaderCell, TableHeaderRow, TableRow } from "@/components/ui/table";
import Tooltip from "@/components/ui/tooltip";
import { useTimezone } from "@/hooks/use-timezone";
import { formatDate, formatTime } from "@/lib/format-data";
import { type AggregatedMeasurementValue, MEASUREMENT_BUCKET_LABELS, MEASUREMENT_RULE_HINTS, MEASUREMENT_RULE_LABELS, type MeasurementAggregationRule, type MeasurementBucketGranularity, aggregateBucketValues, aggregationOf, bucketKeyToDate, formatBucketLabel, groupValuesByBucket } from "@/lib/measurement-aggregation";
import { canRenderBodyGroup } from "@/lib/measurement-body";
import { formatChange, formatMeasurementValue, getFieldChartColor, getSeverityBgColor, getSeverityColor, hashToColor } from "@/lib/measurement-calculations";
import { resolveChartPanelConfigs } from "@/lib/measurement-chart-panels";
import { buildScoreBreakdown } from "@/lib/measurement-score-breakdown";
import { isVendorSourced, vendorReadOnlyReason } from "@/lib/measurement-source";
import { type ResolvedItem, buildFieldMap, compareValuesAscending, computeTotalChange, formatGoalDistance, formatGoalText, getValueTime, isHeightField, isNumericDataType, latestGoalsFromValues, parseTimestamp, resolveGroupMemberships, sortedDates } from "@/lib/measurement-utils";
import { cx } from "@/lib/tailwind-utils";
import type { ChartPanelConfig, ExplicitMeasurementGoal, MeasurementDisplayType, MeasurementField, MeasurementGoal, MeasurementGroupWithFields, MeasurementValueSummary } from "@/types/measurement";
import BodyDisplay from "./body/body-display";
import { Delta } from "./delta-display";
import EditMeasurementValueDialog from "./edit-value-dialog";
import MeasurementGoalDialog from "./goal-dialog";
import { MeasurementSourceBadge } from "./measurement-source-badge";
import { MeasurementValueChips } from "./measurement-value-chips";
import { type MeasurementBucketing, useMeasurementBucketing } from "./use-measurement-bucketing";
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function addWarningMessage(messages: Set<string>, value: unknown) {
  if (typeof value === "string" && value.trim()) {
    messages.add(value.trim());
  }
}
function addWarningMessages(messages: Set<string>, value: unknown) {
  if (Array.isArray(value)) {
    value.forEach(entry => addWarningMessage(messages, entry));
  } else {
    addWarningMessage(messages, value);
  }
}
function collectComputedWarningMessages(detail: unknown): string[] {
  const messages = new Set<string>();
  if (!isRecord(detail)) return [];
  addWarningMessages(messages, detail.warnings);
  for (const collectionKey of ["components", "items"] as const) {
    const collection = detail[collectionKey];
    if (!isRecord(collection)) continue;
    Object.values(collection).forEach(entry => {
      if (!isRecord(entry)) return;
      addWarningMessages(messages, entry.warning);
      addWarningMessages(messages, entry.warnings);
      if (entry.source === "fallback" && typeof entry.fallbackFrom === "string") {
        messages.add(`Used prior value from ${entry.fallbackFrom}`);
      }
    });
  }
  return Array.from(messages);
}
function computedWarningMessages(value: MeasurementValueSummary | null | undefined): string[] {
  return collectComputedWarningMessages(value?.computedDetail);
}
type ResolvedFieldItem = Extract<ResolvedItem, {
  type: "field";
}>;
function fieldItemKey(item: ResolvedFieldItem, index: number): string {
  return item.membership?.id ?? `${item.field.id}-${index}`;
}
function warningBadgeLabel(warnings: string[]): string {
  const usesPriorValue = warnings.some(warning => /prior|fallback|carried/i.test(warning));
  if (usesPriorValue) {
    return warnings.length === 1 ? "Uses prior value" : "Uses prior values";
  }
  return warnings.length === 1 ? "Calculation notice" : `${warnings.length} notices`;
}
function ComputedWarningBadge(t0) {
  const $ = _c(13);
  const {
    warnings
  } = t0;
  if (warnings.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-1.5 text-caption-emphasis text-status-warning-text"><FiAlertTriangle size={12} />Calculation notice</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== warnings) {
    t2 = warnings.map(_temp);
    $[1] = warnings;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="max-w-xs space-y-1.5">{t1}<div className="space-y-1">{t2}</div></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FiAlertTriangle size={12} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== warnings) {
    t5 = warningBadgeLabel(warnings);
    $[6] = warnings;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="inline-flex items-center gap-1 rounded-full bg-status-warning-bg px-1.5 py-0.5 text-tiny font-medium leading-tight text-status-warning-text">{t4}{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <Tooltip variant="light" side="top" content={t3}>{t6}</Tooltip>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
function _temp(warning) {
  return <p key={warning} className="rounded-md bg-status-warning-bg/60 px-2 py-1 text-caption text-foreground-secondary">{warning}</p>;
}
interface MeasurementGroupCardProps {
  group: MeasurementGroupWithFields;
  valuesByDate: Record<string, MeasurementValueSummary[]>;
  rawValuesByDate?: Record<string, MeasurementValueSummary[]>;
  patientId: string;
  onRecord: () => void;
  variant?: "card" | "transparent";
  defaultDisplayType?: MeasurementDisplayType;
  hideChartOption?: boolean;
}
interface MeasurementDetailRow {
  key: string;
  dateKey: string;
  measurementDate: string;
  values: MeasurementValueSummary[];
  valueMap: Map<string, MeasurementValueSummary>;
}
function compareValuesDescending(a: MeasurementValueSummary, b: MeasurementValueSummary) {
  return compareValuesAscending(b, a);
}
function latestValueMap<T extends MeasurementValueSummary>(values: T[]) {
  const map = new Map<string, T>();
  for (const value of [...values].sort(compareValuesAscending)) {
    map.set(value.fieldId, value);
  }
  return map;
}
function measurementDetailValues(values: MeasurementValueSummary[]) {
  return values.filter(value => !value.isDerived);
}
function buildMeasurementDetailRows(dateKey: string, values: MeasurementValueSummary[]): MeasurementDetailRow[] {
  const bucketRows = new Map<string, Omit<MeasurementDetailRow, "valueMap">[]>();
  for (const value of measurementDetailValues(values).sort(compareValuesDescending)) {
    const instantKey = value.measurementDate || value.createdDate || value.id;
    const existingRows = bucketRows.get(instantKey) ?? [];
    const existingRow = existingRows.find(row => !row.values.some(existing => existing.fieldId === value.fieldId));
    if (existingRow) {
      existingRow.values.push(value);
      continue;
    }
    const rowIndex = existingRows.length;
    const row: Omit<MeasurementDetailRow, "valueMap"> = {
      key: `${dateKey}-${instantKey}-${rowIndex}`,
      dateKey,
      measurementDate: value.measurementDate || value.createdDate || "",
      values: [value]
    };
    existingRows.push(row);
    bucketRows.set(instantKey, existingRows);
  }
  return Array.from(bucketRows.values()).flat().map(row => ({
    ...row,
    valueMap: latestValueMap(row.values)
  })).sort((a, b) => {
    const timeDiff = (parseTimestamp(b.measurementDate) ?? 0) - (parseTimestamp(a.measurementDate) ?? 0);
    if (timeDiff !== 0) return timeDiff;
    return a.key.localeCompare(b.key);
  });
}
function formatMeasurementTime(isoString: string): string {
  if (!isoString) return "Recorded";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "Recorded";
  return formatTime(date);
}

/**
 * Detail-row label for a bucket that spans more than one day. At DAY every
 * reading shares the parent row's date so a bare time is unambiguous, but a
 * WEEK bucket lists the whole week's readings — and the day is then the one
 * thing the row would not say. The expander is where an aggregated row sends
 * you to edit a reading, so it has to identify which reading is which.
 */
function formatMeasurementDayAndTime(isoString: string): string {
  if (!isoString) return "Recorded";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "Recorded";
  return `${formatDate(date, {
    month: "short",
    day: "numeric"
  })}, ${formatTime(date)}`;
}

// === Table Display ===
function TableDisplay(t0) {
  const $ = _c(75);
  const {
    group,
    valuesByDate,
    rawValuesByDate,
    granularity,
    timeZone,
    fieldMap,
    patientId,
    latestGoals,
    onOpenGoalDialog,
    previewMode
  } = t0;
  const [editingValue, setEditingValue] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedDates, setExpandedDates] = useState(t1);
  let t2;
  if ($[1] !== valuesByDate) {
    t2 = sortedDates(valuesByDate);
    $[1] = valuesByDate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const dates = t2;
  const detailSourceByDate = rawValuesByDate ?? valuesByDate;
  let map;
  if ($[3] !== valuesByDate) {
    map = new Map();
    const sortedKeys = Object.keys(valuesByDate).sort(_temp2);
    for (const dateKey of sortedKeys) {
      for (const v of valuesByDate[dateKey]) {
        if (!map.has(v.fieldId) && v.numericValue != null) {
          map.set(v.fieldId, v);
        }
      }
    }
    $[3] = valuesByDate;
    $[4] = map;
  } else {
    map = $[4];
  }
  const latestValueByField = map;
  let rows;
  if ($[5] !== dates || $[6] !== detailSourceByDate || $[7] !== valuesByDate) {
    rows = {};
    for (const date of dates) {
      rows[date] = buildMeasurementDetailRows(date, detailSourceByDate[date] ?? valuesByDate[date] ?? []);
    }
    $[5] = dates;
    $[6] = detailSourceByDate;
    $[7] = valuesByDate;
    $[8] = rows;
  } else {
    rows = $[8];
  }
  const detailRowsByDate = rows;
  let t3;
  if ($[9] !== dates || $[10] !== detailRowsByDate) {
    let t4;
    if ($[12] !== detailRowsByDate) {
      t4 = date_0 => detailRowsByDate[date_0] ?? [];
      $[12] = detailRowsByDate;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = dates.flatMap(t4);
    $[9] = dates;
    $[10] = detailRowsByDate;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const allDetailRows = t3;
  let t4;
  if ($[14] !== allDetailRows) {
    t4 = new Map(allDetailRows.map(_temp3));
    $[14] = allDetailRows;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const detailRowIndex = t4;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = date_1 => {
      setExpandedDates(current => {
        const next = new Set(current);
        if (next.has(date_1)) {
          next.delete(date_1);
        } else {
          next.add(date_1);
        }
        return next;
      });
    };
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const toggleDateExpansion = t5;
  let next_0;
  if ($[17] !== dates || $[18] !== detailRowsByDate || $[19] !== detailSourceByDate || $[20] !== valuesByDate) {
    next_0 = new Set();
    for (const date_2 of dates) {
      const detailRows = detailRowsByDate[date_2] ?? [];
      const summaryValues = valuesByDate[date_2] ?? [];
      const rawValues = detailSourceByDate[date_2] ?? summaryValues;
      const rawDetailValues = measurementDetailValues(rawValues);
      const summaryDetailValues = measurementDetailValues(summaryValues);
      if (detailRows.length > 1 || rawDetailValues.length > summaryDetailValues.length) {
        next_0.add(date_2);
      }
    }
    $[17] = dates;
    $[18] = detailRowsByDate;
    $[19] = detailSourceByDate;
    $[20] = valuesByDate;
    $[21] = next_0;
  } else {
    next_0 = $[21];
  }
  const expandableDates = next_0;
  const hasExpandableDates = expandableDates.size > 0;
  let t6;
  if ($[22] !== allDetailRows || $[23] !== detailRowIndex) {
    t6 = (rowKey, fieldId) => {
      const index_0 = detailRowIndex.get(rowKey);
      if (index_0 == null) {
        return;
      }
      for (let i = index_0 + 1; i < allDetailRows.length; i++) {
        const previousValue = allDetailRows[i].valueMap.get(fieldId);
        if (previousValue?.numericValue != null) {
          return previousValue;
        }
      }
    };
    $[22] = allDetailRows;
    $[23] = detailRowIndex;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const getPreviousDetailValue = t6;
  let t7;
  if ($[25] !== group || $[26] !== previewMode) {
    t7 = (val, field, rowChange) => {
      const aggregation = aggregationOf(val);
      const isArray = val.arrayValue && val.arrayValue.length > 0;
      const valueDisplay = isArray ? <MeasurementValueChips values={val.arrayValue ?? []} field={field} testId={`measurement-value-chips-${val.id}`} /> : formatMeasurementValue(val, field);
      const readOnlyReason = vendorReadOnlyReason(val);
      return <div className="flex flex-wrap items-center gap-2">{previewMode || aggregation || field.isDerived || readOnlyReason ? <Tooltip content={aggregation ? `${MEASUREMENT_RULE_LABELS[aggregation.rule]} of ${aggregation.count} readings — expand the row to edit one` : field.isDerived ? "Derived values cannot be edited" : readOnlyReason ?? undefined} triggerAsChild={true}><span className="whitespace-nowrap text-foreground">{valueDisplay}</span></Tooltip> : <button type="button" data-testid="edit-value-button" className="group/edit flex shrink-0 items-center gap-1 whitespace-nowrap text-foreground hover:text-primary" onClick={() => setEditingValue({
          value: val,
          field
        })}><span>{valueDisplay}</span><FiEdit2 size={12} className="shrink-0 opacity-0 transition-opacity group-hover/edit:opacity-100" /></button>}{aggregation && <span className="rounded-full bg-background-secondary px-1.5 py-0.5 text-tiny font-medium leading-tight text-foreground-muted">{MEASUREMENT_RULE_HINTS[aggregation.rule]} · {aggregation.count}</span>}{val.outcome && <span className={cx("rounded-full px-1.5 py-0.5 text-tiny font-medium leading-tight", getSeverityBgColor(val.outcome.severity), getSeverityColor(val.outcome.severity))}>{val.outcome.label}</span>}<MeasurementSourceBadge isUserReported={val.isUserReported} isLabSourced={val.isLabSourced} isWearableSourced={val.isWearableSourced} wearableProvider={val.wearableProvider} fromMultipleDevices={val.fromMultipleDevices} isInbodySourced={val.isInbodySourced} compact={true} /><ComputedWarningBadge warnings={computedWarningMessages(val)} />{group.displayConfig?.showDeltas !== false && rowChange != null && rowChange !== 0 && <Delta change={rowChange} field={field} size="xs"><Delta.Value />{" "}{group.displayConfig?.showTrends !== false && <Delta.Icon />}</Delta>}</div>;
    };
    $[25] = group;
    $[26] = previewMode;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const renderValueContent = t7;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t8;
  let t9;
  if ($[28] !== dates || $[29] !== detailRowsByDate || $[30] !== expandableDates || $[31] !== expandedDates || $[32] !== fieldMap || $[33] !== getPreviousDetailValue || $[34] !== granularity || $[35] !== group || $[36] !== hasExpandableDates || $[37] !== latestGoals || $[38] !== latestValueByField || $[39] !== onOpenGoalDialog || $[40] !== previewMode || $[41] !== renderValueContent || $[42] !== timeZone || $[43] !== valuesByDate) {
    t13 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedItems = resolveGroupMemberships(group);
      const fieldItems = resolvedItems.filter(_temp4);
      if (dates.length === 0) {
        let t14;
        if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = <p className="py-4 text-center text-body text-muted-foreground">No measurements recorded yet.</p>;
          $[51] = t14;
        } else {
          t14 = $[51];
        }
        t13 = t14;
        break bb0;
      }
      t12 = "overflow-x-auto";
      T0 = Table;
      t8 = "md:min-w-[800px]";
      const t14 = group.displayConfig?.showTime ? "Date & Time" : "Date";
      let t15;
      if ($[52] !== t14) {
        t15 = <TableHeaderCell className="whitespace-nowrap">{t14}</TableHeaderCell>;
        $[52] = t14;
        $[53] = t15;
      } else {
        t15 = $[53];
      }
      let t16;
      if ($[54] !== fieldMap || $[55] !== latestGoals || $[56] !== latestValueByField || $[57] !== onOpenGoalDialog || $[58] !== previewMode) {
        t16 = (item_0, fieldIndex) => {
          const fieldGoal = latestGoals.get(item_0.field.id);
          const field_0 = fieldMap.get(item_0.field.id);
          const latestVal = latestValueByField.get(item_0.field.id);
          const dp = item_0.field.decimalPlaces;
          const distance = fieldGoal ? formatGoalDistance(fieldGoal, latestVal?.numericValue, dp) : null;
          const goalTextWithDistance = fieldGoal ? formatGoalText(fieldGoal, item_0.field.unit, dp) + (distance ? ` (${distance})` : "") : null;
          return <TableHeaderCell key={fieldItemKey(item_0, fieldIndex)} className="whitespace-nowrap"><span className="flex items-center gap-1">{item_0.field.isDerived && <TbCalculator size={12} className="text-foreground-muted" />}{item_0.field.name}{item_0.field.unit && <span className="font-normal normal-case">({isHeightField(item_0.field) ? <span>{"ft/in"}</span> : <span>{item_0.field.unit}</span>}<span>)</span></span>}{!previewMode && field_0 && isNumericDataType(field_0.dataType) && (fieldGoal?.isComputed ? <Tooltip content="Auto-computed from related goals" side="top"><button type="button" data-testid="goal-button" className="flex shrink-0 items-center gap-0.5 text-status-success-text transition-colors hover:text-status-success-text/80" onClick={e => {
                  e.stopPropagation();
                  onOpenGoalDialog(field_0, null);
                }} title="Set goal"><TbTarget size={14} /><span className="text-tiny">{goalTextWithDistance}</span></button></Tooltip> : <button type="button" data-testid="goal-button" className={cx("flex shrink-0 items-center gap-0.5 transition-colors", fieldGoal ? "text-status-success-text hover:text-status-success-text/80" : "text-foreground-muted hover:text-foreground-secondary")} onClick={e_0 => {
                e_0.stopPropagation();
                onOpenGoalDialog(field_0, fieldGoal);
              }} title="Set goal"><TbTarget size={14} /><span className="text-tiny">{goalTextWithDistance ?? "Goal"}</span></button>)}</span></TableHeaderCell>;
        };
        $[54] = fieldMap;
        $[55] = latestGoals;
        $[56] = latestValueByField;
        $[57] = onOpenGoalDialog;
        $[58] = previewMode;
        $[59] = t16;
      } else {
        t16 = $[59];
      }
      t9 = <TableHead><TableHeaderRow className="[&>*]:px-3 [&>*:first-child]:pl-3 [&>*:not(:last-child)]:pr-3">{t15}{fieldItems.map(t16)}</TableHeaderRow></TableHead>;
      t10 = <TableBody>{dates.map((date_3, dateIdx) => {
          const values = valuesByDate[date_3];
          const valMap = latestValueMap(values);
          const detailRows_0 = detailRowsByDate[date_3] ?? [];
          const isExpandable = expandableDates.has(date_3);
          const isExpanded = expandedDates.has(date_3);
          return <Fragment key={date_3}><TableRow className="hover:bg-row-hover [&>*]:px-3 [&>*:first-child]:pl-3 [&>*:not(:last-child)]:pr-3"><TableCell className="whitespace-nowrap"><div className="flex items-center gap-1.5">{isExpandable ? <button type="button" aria-expanded={isExpanded} aria-label={isExpanded ? `Collapse measurements for ${date_3}` : `Expand measurements for ${date_3}`} className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-foreground-muted transition-colors hover:bg-hover hover:text-foreground" onClick={() => toggleDateExpansion(date_3)}>{isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</button> : hasExpandableDates ? <span className="h-5 w-5 shrink-0" /> : null}<span>{formatBucketLabel(date_3, granularity, timeZone, "long")}{group.displayConfig?.showTime && granularity === "DAY" && (() => {
                      const latest = values.reduce(_temp5);
                      return latest?.measurementDate ? <span className="ml-1 text-foreground-muted">{formatTime(latest.measurementDate)}</span> : null;
                    })()}</span>{isExpandable && <span className="rounded-full bg-background-secondary px-1.5 py-0.5 text-tiny text-foreground-muted">{detailRows_0.length}{" "}{detailRows_0.length === 1 ? <span>{"reading"}</span> : <span>{"readings"}</span>}</span>}</div></TableCell>{fieldItems.map((item_1, fieldIndex_0) => {
                const val_0 = valMap.get(item_1.field.id);
                const field_1 = fieldMap.get(item_1.field.id);
                if (!val_0 || !field_1) {
                  return <TableCell key={fieldItemKey(item_1, fieldIndex_0)} className="whitespace-nowrap text-foreground-muted">—</TableCell>;
                }
                const prevDate = dates[dateIdx + 1];
                const prevVal = prevDate ? valuesByDate[prevDate]?.find(v_0 => v_0.fieldId === item_1.field.id) : undefined;
                const rowChange_0 = val_0.numericValue != null && prevVal?.numericValue != null ? val_0.numericValue - prevVal.numericValue : null;
                return <TableCell key={fieldItemKey(item_1, fieldIndex_0)}>{renderValueContent(val_0, field_1, rowChange_0)}</TableCell>;
              })}</TableRow>{isExpanded && detailRows_0.map(detailRow => <TableRow key={detailRow.key} className="bg-background-secondary/30 hover:bg-row-hover [&>*]:px-3 [&>*:first-child]:pl-3 [&>*:not(:last-child)]:pr-3"><TableCell className="whitespace-nowrap text-foreground-muted"><div className="flex items-center gap-1.5 pl-6"><span aria-hidden="true" className="h-px w-3 bg-border" /><span>{granularity === "DAY" ? formatMeasurementTime(detailRow.measurementDate) : formatMeasurementDayAndTime(detailRow.measurementDate)}</span></div></TableCell>{fieldItems.map((item_2, fieldIndex_1) => {
                const val_1 = detailRow.valueMap.get(item_2.field.id);
                const field_2 = fieldMap.get(item_2.field.id);
                if (!val_1 || !field_2) {
                  return <TableCell key={fieldItemKey(item_2, fieldIndex_1)} className="whitespace-nowrap text-foreground-muted">—</TableCell>;
                }
                const prevVal_0 = getPreviousDetailValue(detailRow.key, item_2.field.id);
                const rowChange_1 = val_1.numericValue != null && prevVal_0?.numericValue != null ? val_1.numericValue - prevVal_0.numericValue : null;
                return <TableCell key={fieldItemKey(item_2, fieldIndex_1)}>{renderValueContent(val_1, field_2, rowChange_1)}</TableCell>;
              })}</TableRow>)}</Fragment>;
        })}</TableBody>;
      t11 = group.displayConfig?.showDeltas !== false && dates.length > 1 && <TableFoot><tr className="[&>*]:px-3 [&>*:first-child]:pl-3 [&>*:not(:last-child)]:pr-3"><TableCell className="whitespace-nowrap text-caption-strong uppercase text-foreground-muted">Total Change</TableCell>{fieldItems.map((item_3, fieldIndex_2) => {
            const field_3 = fieldMap.get(item_3.field.id);
            if (!field_3 || !isNumericDataType(field_3.dataType)) {
              return <TableCell key={fieldItemKey(item_3, fieldIndex_2)} className="whitespace-nowrap text-foreground-muted">—</TableCell>;
            }
            const change = computeTotalChange(valuesByDate, dates, item_3.field.id);
            if (change == null) {
              return <TableCell key={fieldItemKey(item_3, fieldIndex_2)} className="whitespace-nowrap text-foreground-muted">—</TableCell>;
            }
            return <TableCell key={fieldItemKey(item_3, fieldIndex_2)} className="whitespace-nowrap"><span className="text-caption-strong text-foreground-secondary">{formatChange(change, field_3)}</span></TableCell>;
          })}</tr></TableFoot>;
    }
    $[28] = dates;
    $[29] = detailRowsByDate;
    $[30] = expandableDates;
    $[31] = expandedDates;
    $[32] = fieldMap;
    $[33] = getPreviousDetailValue;
    $[34] = granularity;
    $[35] = group;
    $[36] = hasExpandableDates;
    $[37] = latestGoals;
    $[38] = latestValueByField;
    $[39] = onOpenGoalDialog;
    $[40] = previewMode;
    $[41] = renderValueContent;
    $[42] = timeZone;
    $[43] = valuesByDate;
    $[44] = T0;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t8;
    $[50] = t9;
  } else {
    T0 = $[44];
    t10 = $[45];
    t11 = $[46];
    t12 = $[47];
    t13 = $[48];
    t8 = $[49];
    t9 = $[50];
  }
  if (t13 !== Symbol.for("react.early_return_sentinel")) {
    return t13;
  }
  let t14;
  if ($[60] !== T0 || $[61] !== t10 || $[62] !== t11 || $[63] !== t8 || $[64] !== t9) {
    t14 = <T0 className={t8}>{t9}{t10}{t11}</T0>;
    $[60] = T0;
    $[61] = t10;
    $[62] = t11;
    $[63] = t8;
    $[64] = t9;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== t12 || $[67] !== t14) {
    t15 = <div className={t12}>{t14}</div>;
    $[66] = t12;
    $[67] = t14;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  let t16;
  if ($[69] !== editingValue || $[70] !== patientId) {
    t16 = editingValue && <EditMeasurementValueDialog value={editingValue.value} field={editingValue.field} patientId={patientId} open={!!editingValue} onOpenChange={open => {
      if (!open) {
        setEditingValue(null);
      }
    }} />;
    $[69] = editingValue;
    $[70] = patientId;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  let t17;
  if ($[72] !== t15 || $[73] !== t16) {
    t17 = <>{t15}{t16}</>;
    $[72] = t15;
    $[73] = t16;
    $[74] = t17;
  } else {
    t17 = $[74];
  }
  return t17;
}

// === Chart Display ===
function _temp5(a_0, b_0) {
  return getValueTime(a_0) >= getValueTime(b_0) ? a_0 : b_0;
}
function _temp4(item) {
  return item.type === "field";
}
function _temp3(row, index) {
  return [row.key, index];
}
function _temp2(a, b) {
  return new Date(b).getTime() - new Date(a).getTime();
}
function ChartDisplay(t0) {
  const $ = _c(31);
  const {
    group,
    valuesByDate,
    granularity,
    timeZone,
    fieldMap,
    latestGoals,
    extraAnnotations,
    previewMode
  } = t0;
  const resolvedItems = resolveGroupMemberships(group);
  const allFieldItems = resolvedItems.filter(_temp6);
  const seenChartFieldIds = new Set();
  const numericFields = allFieldItems.filter(item_0 => {
    if (!isNumericDataType(item_0.field.dataType)) {
      return false;
    }
    if (seenChartFieldIds.has(item_0.field.id)) {
      return false;
    }
    seenChartFieldIds.add(item_0.field.id);
    return true;
  });
  group.displayConfig?.chartPanels;
  const t1 = group.displayConfig?.chartPanels;
  let t2;
  if ($[0] !== fieldMap || $[1] !== t1) {
    t2 = resolveChartPanelConfigs(t1, fieldMap);
    $[0] = fieldMap;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const panels = t2;
  let t3;
  bb0: {
    if (!panels?.length) {
      t3 = null;
      break bb0;
    }
    let t4;
    if ($[3] !== panels) {
      t4 = Array.from(new Set(panels.flatMap(_temp7)));
      $[3] = panels;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t3 = t4;
  }
  const panelFieldIds = t3;
  const defaultFieldIds = group.displayConfig?.defaultFields ?? (panelFieldIds && panelFieldIds.length > 0 ? panelFieldIds : previewMode && numericFields[0] ? [numericFields[0].field.id] : numericFields.map(_temp8));
  const [visibleFields, setVisibleFields] = useState(new Set(defaultFieldIds));
  let dates;
  let formatChartDate;
  let t4;
  if ($[5] !== granularity || $[6] !== timeZone || $[7] !== valuesByDate || $[8] !== visibleFields) {
    dates = sortedDates(valuesByDate).reverse();
    let t5;
    if ($[12] !== granularity || $[13] !== timeZone) {
      t5 = (date, style) => formatBucketLabel(date, granularity, timeZone, style);
      $[12] = granularity;
      $[13] = timeZone;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    formatChartDate = t5;
    let t6;
    if ($[15] !== valuesByDate || $[16] !== visibleFields) {
      t6 = date_0 => {
        const values = valuesByDate[date_0];
        const sourceByFieldId = {};
        const entry = {
          date: date_0,
          sourceByFieldId
        };
        for (const v of values) {
          if (visibleFields.has(v.fieldId) && v.numericValue != null) {
            entry[v.fieldId] = v.numericValue;
            sourceByFieldId[v.fieldId] = {
              isUserReported: Boolean(v.isUserReported)
            };
          }
        }
        return entry;
      };
      $[15] = valuesByDate;
      $[16] = visibleFields;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t4 = dates.map(t6);
    $[5] = granularity;
    $[6] = timeZone;
    $[7] = valuesByDate;
    $[8] = visibleFields;
    $[9] = dates;
    $[10] = formatChartDate;
    $[11] = t4;
  } else {
    dates = $[9];
    formatChartDate = $[10];
    t4 = $[11];
  }
  const chartData = t4;
  const hasScoreDonutPanel = panels?.some(_temp9) ?? false;
  const hasData = hasScoreDonutPanel ? dates.length >= 1 : chartData.length >= 2;
  let latest;
  if ($[18] !== valuesByDate) {
    latest = new Map();
    for (const date_1 of sortedDates(valuesByDate)) {
      for (const value of valuesByDate[date_1]) {
        if (!latest.has(value.fieldId)) {
          latest.set(value.fieldId, value);
        }
      }
    }
    $[18] = valuesByDate;
    $[19] = latest;
  } else {
    latest = $[19];
  }
  const latestValuesByField = latest;
  const getFieldColor = _temp10;
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = fieldId => {
      setVisibleFields(prev => {
        const next = new Set(prev);
        if (next.has(fieldId)) {
          next.delete(fieldId);
        } else {
          next.add(fieldId);
        }
        return next;
      });
    };
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const toggleField = t5;
  const isMultiPanel = panels && panels.length > 1;
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (items, panel_1) => items.map(item_3 => ({
      id: item_3.field.id,
      name: item_3.field.name,
      color: panel_1?.fieldOverrides?.[item_3.field.id]?.color ?? getFieldColor(item_3)
    }));
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const toPillFields = t6;
  const singlePanelItems = panels?.length === 1 ? numericFields.filter(item_4 => panels[0].fieldIds.includes(item_4.field.id)) : numericFields;
  let t7;
  if ($[22] !== fieldMap) {
    t7 = breakdown => {
      const colors = {};
      breakdown.entries.forEach(entry_0 => {
        const field = entry_0.operandId ? fieldMap.get(entry_0.operandId) : null;
        colors[entry_0.key] = field ? getFieldChartColor(field) : hashToColor(entry_0.operandId ?? entry_0.key);
      });
      return colors;
    };
    $[22] = fieldMap;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const buildScoreDonutColors = t7;
  const buildChartProps = panel_2 => {
    const filtered = numericFields.filter(item_5 => {
      if (!visibleFields.has(item_5.field.id)) {
        return false;
      }
      if (panel_2) {
        return panel_2.fieldIds.includes(item_5.field.id);
      }
      return true;
    });
    const chartFields = filtered.map(item_6 => ({
      id: item_6.field.id,
      name: item_6.field.name,
      color: panel_2?.fieldOverrides?.[item_6.field.id]?.color ?? getFieldColor(item_6)
    }));
    const singleField = filtered.length === 1;
    const annotations = [...filtered.flatMap(item_7 => {
      const goal = latestGoals?.get(item_7.field.id);
      return goalElements(goal, {
        showLabels: singleField
      });
    }), ...(extraAnnotations ?? [])];
    return {
      chartFields,
      annotations
    };
  };
  const t8 = "space-y-4";
  const t9 = !isMultiPanel && <FieldTogglePills fields={toPillFields(singlePanelItems, panels?.[0])} selected={visibleFields} onToggle={toggleField} />;
  const T0 = ChartWrapper;
  const t10 = false;
  const t11 = "h-56";
  const t12 = panels && panels.length > 0 ? <div className={cx("space-y-4", panels.length === 1 && "space-y-0")}>{panels.map(panel_3 => {
      const panelFieldSet = new Set(panel_3.fieldIds);
      const panelFields = numericFields.filter(item_8 => panelFieldSet.has(item_8.field.id));
      if (panel_3.chartType === "SCORE_DONUT") {
        const scoreFields = panelFields.filter(item_9 => visibleFields.has(item_9.field.id));
        return <div key={panel_3.id} className="space-y-2"><div className="mb-1 flex items-center gap-2">{isMultiPanel && panel_3.label && <p className="text-caption-strong text-foreground-secondary">{panel_3.label}</p>}</div>{isMultiPanel && <div className="mb-2"><FieldTogglePills fields={toPillFields(panelFields, panel_3)} selected={visibleFields} onToggle={toggleField} /></div>}{scoreFields.length === 0 ? <p className="rounded-lg border border-dashed border-border p-4 text-center text-body text-foreground-muted">Select a score field to see the breakdown.</p> : <div className="space-y-3">{scoreFields.map(t13 => {
              const {
                field: field_0
              } = t13;
              const value_0 = latestValuesByField.get(field_0.id);
              const breakdown_0 = buildScoreBreakdown(value_0?.computedDetail);
              if (!value_0 || !breakdown_0) {
                return <p key={field_0.id} className="rounded-lg border border-dashed border-border p-4 text-center text-body text-foreground-muted">Score breakdown will appear once this computed score has details.</p>;
              }
              return <ScoreDonutChart key={field_0.id} title={field_0.name} score={value_0.numericValue} unit={field_0.unit} decimalPlaces={field_0.decimalPlaces} outcomeLabel={value_0.outcome?.label ?? null} breakdown={breakdown_0} colorsByKey={buildScoreDonutColors(breakdown_0)} fieldMap={fieldMap} />;
            })}</div>}</div>;
      }
      const {
        chartFields: chartFields_0,
        annotations: annotations_0
      } = buildChartProps(panel_3);
      return <div key={panel_3.id}><div className="mb-1 flex items-center gap-2">{isMultiPanel && panel_3.label && <p className="text-caption-strong text-foreground-secondary">{panel_3.label}</p>}</div>{isMultiPanel && <div className="mb-2"><FieldTogglePills fields={toPillFields(panelFields, panel_3)} selected={visibleFields} onToggle={toggleField} /></div>}<div className="h-56"><MeasurementPanelChart chartType={panel_3.chartType} data={chartData} fields={chartFields_0} panel={panel_3} fieldMap={fieldMap} annotations={annotations_0} cartesianOpts={{
            formatDate: formatChartDate
          }} /></div></div>;
    })}</div> : <div className="h-56">{(() => {
      const {
        chartFields: chartFields_1,
        annotations: annotations_1
      } = buildChartProps();
      return <MeasurementPanelChart chartType="LINE" data={chartData} fields={chartFields_1} fieldMap={fieldMap} annotations={annotations_1} cartesianOpts={{
        formatDate: formatChartDate
      }} />;
    })()}</div>;
  let t14;
  if ($[24] !== T0 || $[25] !== hasData || $[26] !== t12) {
    t14 = <T0 isLoading={t10} isDataLoaded={hasData} loadingClassName={t11}>{t12}</T0>;
    $[24] = T0;
    $[25] = hasData;
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t9) {
    t15 = <div className={t8}>{t9}{t14}</div>;
    $[28] = t14;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}

// === Cards Display ===
function _temp10(item_2) {
  return getFieldChartColor(item_2.field);
}
function _temp9(panel_0) {
  return panel_0.chartType === "SCORE_DONUT";
}
function _temp8(item_1) {
  return item_1.field.id;
}
function _temp7(panel) {
  return panel.fieldIds;
}
function _temp6(item) {
  return item.type === "field";
}
function CardsDisplay(t0) {
  const $ = _c(62);
  const {
    group,
    valuesByDate,
    granularity,
    timeZone,
    patientId,
    latestGoals,
    onOpenGoalDialog,
    previewMode
  } = t0;
  const [editingValue, setEditingValue] = useState(null);
  let change;
  let firstDateMap;
  let latest;
  let latestDateMap;
  let prevDateMap;
  let totalChange;
  if ($[0] !== valuesByDate) {
    latest = new Map();
    latestDateMap = new Map();
    change = new Map();
    prevDateMap = new Map();
    firstDateMap = new Map();
    const ascDates = sortedDates(valuesByDate).reverse();
    for (const date of ascDates) {
      for (const v of valuesByDate[date]) {
        latest.set(v.fieldId, v);
        latestDateMap.set(v.fieldId, date);
      }
    }
    const descDates = sortedDates(valuesByDate);
    latest.forEach((_val, fieldId) => {
      let foundLatest = false;
      for (const date_0 of descDates) {
        const val = valuesByDate[date_0].find(v_0 => v_0.fieldId === fieldId);
        if (val?.numericValue != null) {
          if (!foundLatest) {
            foundLatest = true;
          } else {
            const latestVal = latest.get(fieldId);
            if (latestVal?.numericValue != null) {
              change.set(fieldId, latestVal.numericValue - val.numericValue);
            }
            prevDateMap.set(fieldId, date_0);
            break;
          }
        }
      }
    });
    totalChange = new Map();
    latest.forEach((_val_0, fieldId_0) => {
      totalChange.set(fieldId_0, computeTotalChange(valuesByDate, descDates, fieldId_0));
      for (let i = descDates.length - 1; i >= 0; i--) {
        const val_0 = valuesByDate[descDates[i]].find(v_1 => v_1.fieldId === fieldId_0);
        if (val_0?.numericValue != null) {
          firstDateMap.set(fieldId_0, descDates[i]);
          break;
        }
      }
    });
    $[0] = valuesByDate;
    $[1] = change;
    $[2] = firstDateMap;
    $[3] = latest;
    $[4] = latestDateMap;
    $[5] = prevDateMap;
    $[6] = totalChange;
  } else {
    change = $[1];
    firstDateMap = $[2];
    latest = $[3];
    latestDateMap = $[4];
    prevDateMap = $[5];
    totalChange = $[6];
  }
  let t1;
  if ($[7] !== change || $[8] !== firstDateMap || $[9] !== latest || $[10] !== latestDateMap || $[11] !== prevDateMap || $[12] !== totalChange) {
    t1 = {
      latestByField: latest,
      latestDateByField: latestDateMap,
      changeByField: change,
      totalChangeByField: totalChange,
      prevDateByField: prevDateMap,
      firstDateByField: firstDateMap
    };
    $[7] = change;
    $[8] = firstDateMap;
    $[9] = latest;
    $[10] = latestDateMap;
    $[11] = prevDateMap;
    $[12] = totalChange;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const {
    latestByField,
    latestDateByField,
    changeByField,
    totalChangeByField,
    prevDateByField,
    firstDateByField
  } = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[14] !== changeByField || $[15] !== firstDateByField || $[16] !== granularity || $[17] !== group || $[18] !== latestByField || $[19] !== latestDateByField || $[20] !== latestGoals || $[21] !== onOpenGoalDialog || $[22] !== prevDateByField || $[23] !== previewMode || $[24] !== timeZone || $[25] !== totalChangeByField) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedItems = resolveGroupMemberships(group);
      const columns = group.displayConfig?.columns ?? 2;
      if (latestByField.size === 0) {
        let t6;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <p className="py-4 text-center text-body text-muted-foreground">No measurements recorded yet.</p>;
          $[30] = t6;
        } else {
          t6 = $[30];
        }
        t5 = t6;
        break bb0;
      }
      t4 = "space-y-3";
      const t6 = columns === 1 && "grid-cols-1";
      const t7 = columns === 2 && "grid-cols-2";
      const t8 = columns === 3 && "grid-cols-3";
      const t9 = columns === 4 && "grid-cols-4";
      if ($[31] !== t6 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
        t2 = cx("grid gap-3", t6, t7, t8, t9);
        $[31] = t6;
        $[32] = t7;
        $[33] = t8;
        $[34] = t9;
        $[35] = t2;
      } else {
        t2 = $[35];
      }
      let t10;
      if ($[36] !== changeByField || $[37] !== columns || $[38] !== firstDateByField || $[39] !== granularity || $[40] !== group.displayConfig?.showDeltas || $[41] !== group.displayConfig?.showTime || $[42] !== group.displayConfig?.showTrends || $[43] !== latestByField || $[44] !== latestDateByField || $[45] !== latestGoals || $[46] !== onOpenGoalDialog || $[47] !== prevDateByField || $[48] !== previewMode || $[49] !== timeZone || $[50] !== totalChangeByField) {
        t10 = (resolvedItem, idx) => {
          if (resolvedItem.type === "section-header") {
            return <div key={`section-${idx}`} className={cx("flex items-center gap-2 border-b border-border pb-1 pt-2", columns === 1 && "col-span-1", columns === 2 && "col-span-2", columns === 3 && "col-span-3", columns === 4 && "col-span-4")}>{resolvedItem.groupColor && <span className="inline-block h-2 w-2 rounded-full" style={{
                backgroundColor: resolvedItem.groupColor
              }} />}<span className="text-caption-emphasis text-foreground-secondary">{resolvedItem.groupName}</span></div>;
          }
          const field = resolvedItem.field;
          const val_1 = latestByField.get(field.id);
          const fieldChange = changeByField.get(field.id) ?? null;
          const totalFieldChange = totalChangeByField.get(field.id) ?? null;
          const prevDate = prevDateByField.get(field.id);
          const firstDate = firstDateByField.get(field.id);
          const latestDate = latestDateByField.get(field.id);
          const prevBucketDate = prevDate ? bucketKeyToDate(prevDate) : null;
          const aggregation = val_1 ? aggregationOf(val_1) : null;
          const warnings = computedWarningMessages(val_1);
          return <div key={fieldItemKey(resolvedItem, idx)} className="rounded-lg border border-border bg-background p-3"><div className="flex items-center justify-between gap-1"><span className="min-w-0 truncate text-caption-strong text-muted-foreground">{field.name}</span>{!previewMode && <DropdownMenu><DropdownMenuTrigger asChild={true}><DropdownSettingsButton size="sm" /></DropdownMenuTrigger><DropdownMenuContent align="end">{isNumericDataType(field.dataType) && <DropdownMenuItem data-testid="goal-button" icon={<TbTarget size={14} />} onSelect={() => {
                    const goal = latestGoals.get(field.id);
                    onOpenGoalDialog(field, goal?.isComputed ? null : goal);
                  }}>{latestGoals.has(field.id) && !latestGoals.get(field.id)?.isComputed ? "Edit Goal" : "Set Goal"}</DropdownMenuItem>}{val_1 && !aggregation && !field.isDerived && !isVendorSourced(val_1) && <DropdownMenuItem data-testid="edit-value-button" icon={<FiEdit2 size={14} />} onSelect={() => setEditingValue({
                    value: val_1,
                    field
                  })}>Edit Value</DropdownMenuItem>}</DropdownMenuContent></DropdownMenu>}</div><div className="mt-1 flex items-baseline gap-2">{val_1 && val_1.arrayValue && val_1.arrayValue.length > 0 ? <MeasurementValueChips values={val_1.arrayValue} field={field} testId={`measurement-value-chips-${val_1.id}`} /> : <span className="text-h3 text-foreground">{val_1 ? formatMeasurementValue(val_1, field) : "\u2014"}</span>}</div>{aggregation && latestDate ? <p className="text-caption text-foreground-muted">{formatBucketLabel(latestDate, granularity, timeZone, "long")}<span className="ml-1">({MEASUREMENT_RULE_HINTS[aggregation.rule]} of{" "}{aggregation.count})</span></p> : val_1?.measurementDate && <p className="text-caption text-foreground-muted">{formatDate(val_1.measurementDate)}{group.displayConfig?.showTime && <span className="ml-1">{formatTime(val_1.measurementDate)}</span>}</p>}{latestGoals.has(field.id) && (() => {
              const fieldGoal = latestGoals.get(field.id);
              const dp = field.decimalPlaces;
              const distance = formatGoalDistance(fieldGoal, val_1?.numericValue, dp);
              const goalText = formatGoalText(fieldGoal, field.unit, dp);
              const distanceText = distance ? ` (${distance})` : "";
              const content = <p className="mt-0.5 text-caption text-status-success-text">{goalText}<span className="text-tiny text-foreground-muted">{distanceText}</span></p>;
              return fieldGoal.isComputed ? <Tooltip content="Auto-computed from related goals" side="top">{content}</Tooltip> : content;
            })()}{val_1 && <div className="mt-1.5 flex flex-col gap-1"><div className="flex flex-wrap items-center gap-2">{group.displayConfig?.showDeltas !== false && fieldChange != null && <Delta change={fieldChange} field={field} size="sm" className="flex shrink-0 items-center gap-1">{group.displayConfig?.showTrends !== false && <Delta.Icon />}{" "}<Delta.Value />{prevBucketDate && <Delta.Since date={prevBucketDate} />}</Delta>}{val_1.outcome && <span className={cx("rounded-full px-1.5 py-0.5 text-tiny font-medium leading-tight", getSeverityBgColor(val_1.outcome.severity), getSeverityColor(val_1.outcome.severity))}>{val_1.outcome.label}</span>}<MeasurementSourceBadge isUserReported={val_1.isUserReported} isLabSourced={val_1.isLabSourced} isWearableSourced={val_1.isWearableSourced} wearableProvider={val_1.wearableProvider} fromMultipleDevices={val_1.fromMultipleDevices} isInbodySourced={val_1.isInbodySourced} /><ComputedWarningBadge warnings={warnings} /></div>{group.displayConfig?.showDeltas !== false && totalFieldChange != null && totalFieldChange !== fieldChange && firstDate && <span className="text-body text-foreground-muted">{formatChange(totalFieldChange, field)} since{" "}{formatBucketLabel(firstDate, granularity, timeZone, "long")}</span>}</div>}</div>;
        };
        $[36] = changeByField;
        $[37] = columns;
        $[38] = firstDateByField;
        $[39] = granularity;
        $[40] = group.displayConfig?.showDeltas;
        $[41] = group.displayConfig?.showTime;
        $[42] = group.displayConfig?.showTrends;
        $[43] = latestByField;
        $[44] = latestDateByField;
        $[45] = latestGoals;
        $[46] = onOpenGoalDialog;
        $[47] = prevDateByField;
        $[48] = previewMode;
        $[49] = timeZone;
        $[50] = totalChangeByField;
        $[51] = t10;
      } else {
        t10 = $[51];
      }
      t3 = resolvedItems.map(t10);
    }
    $[14] = changeByField;
    $[15] = firstDateByField;
    $[16] = granularity;
    $[17] = group;
    $[18] = latestByField;
    $[19] = latestDateByField;
    $[20] = latestGoals;
    $[21] = onOpenGoalDialog;
    $[22] = prevDateByField;
    $[23] = previewMode;
    $[24] = timeZone;
    $[25] = totalChangeByField;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
  } else {
    t2 = $[26];
    t3 = $[27];
    t4 = $[28];
    t5 = $[29];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[52] !== t2 || $[53] !== t3) {
    t6 = <div className={t2}>{t3}</div>;
    $[52] = t2;
    $[53] = t3;
    $[54] = t6;
  } else {
    t6 = $[54];
  }
  let t7;
  if ($[55] !== editingValue || $[56] !== patientId) {
    t7 = editingValue && <EditMeasurementValueDialog value={editingValue.value} field={editingValue.field} patientId={patientId} open={!!editingValue} onOpenChange={open => {
      if (!open) {
        setEditingValue(null);
      }
    }} />;
    $[55] = editingValue;
    $[56] = patientId;
    $[57] = t7;
  } else {
    t7 = $[57];
  }
  let t8;
  if ($[58] !== t4 || $[59] !== t6 || $[60] !== t7) {
    t8 = <div className={t4}>{t6}{t7}</div>;
    $[58] = t4;
    $[59] = t6;
    $[60] = t7;
    $[61] = t8;
  } else {
    t8 = $[61];
  }
  return t8;
}
const RENDERABLE_DISPLAY_TYPES = new Set<MeasurementDisplayType>(["TABLE", "CHART", "CARDS", "BODY"]);
function resolveRenderableDisplayType(displayType: MeasurementDisplayType, canRenderBody: boolean): "TABLE" | "CHART" | "CARDS" | "BODY" {
  if (displayType === "GRID") return "CARDS";
  if (!RENDERABLE_DISPLAY_TYPES.has(displayType)) return "TABLE";
  if (displayType === "BODY" && !canRenderBody) return "TABLE";
  return displayType;
}

// === Presentational Content Component ===
// Used by the main card and by the group editor live preview

export interface MeasurementGroupCardContentProps {
  group: MeasurementGroupWithFields;
  valuesByDate: Record<string, MeasurementValueSummary[]>;
  rawValuesByDate?: Record<string, MeasurementValueSummary[]>;
  displayType: MeasurementDisplayType;
  patientId?: string;
  onDisplayTypeChange?: (type: MeasurementDisplayType) => void;
  onOpenGoalDialog?: (field: MeasurementField, goal?: MeasurementGoal | null) => void;
  /** When true, hides interactive features like editing, goals, trends */
  previewMode?: boolean;
  /** Pre-computed field map. Computed internally if not provided. */
  fieldMap?: Map<string, MeasurementField>;
  /** Pre-computed latest goals. Computed internally if not provided. */
  latestGoals?: Map<string, MeasurementGoal>;
  /** Extra chart annotations (e.g. reference range areas) merged into each panel. */
  extraAnnotations?: React.ReactNode[];
  /** When true, hides the chart display type option from the toggle */
  hideChartOption?: boolean;
  /** Shared time-bucket selection. Owns its own DAY/LATEST state when omitted. */
  bucketing?: MeasurementBucketing;
}
export function MeasurementGroupCardContent(t0) {
  const $ = _c(80);
  const {
    group,
    valuesByDate,
    rawValuesByDate,
    displayType,
    patientId,
    onDisplayTypeChange,
    onOpenGoalDialog,
    previewMode: t1,
    fieldMap: fieldMapProp,
    latestGoals: latestGoalsProp,
    extraAnnotations,
    hideChartOption: t2,
    bucketing: bucketingProp
  } = t0;
  const previewMode = t1 === undefined ? false : t1;
  const hideChartOption = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== group) {
    t3 = buildFieldMap(group);
    $[0] = group;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const fieldMapFallback = t3;
  const fieldMap = fieldMapProp ?? fieldMapFallback;
  let t4;
  if ($[2] !== group) {
    t4 = canRenderBodyGroup(group);
    $[2] = group;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const canRenderBody = t4;
  let t5;
  if ($[4] !== canRenderBody || $[5] !== displayType) {
    t5 = resolveRenderableDisplayType(displayType, canRenderBody);
    $[4] = canRenderBody;
    $[5] = displayType;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const renderDisplayType = t5;
  let t6;
  if ($[7] !== valuesByDate) {
    t6 = latestGoalsFromValues(valuesByDate);
    $[7] = valuesByDate;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const latestGoalsFallback = t6;
  const latestGoals = latestGoalsProp ?? latestGoalsFallback;
  const timezone = useTimezone();
  const bucketingFallback = useMeasurementBucketing();
  const {
    granularity,
    rule,
    effectiveRule,
    setGranularity,
    setRule
  } = bucketingProp ?? bucketingFallback;
  let t7;
  bb0: {
    if (granularity === "DAY" && effectiveRule === "LATEST") {
      let t8;
      if ($[9] !== rawValuesByDate || $[10] !== valuesByDate) {
        t8 = {
          bucketedValues: valuesByDate,
          rawBucketedValues: rawValuesByDate
        };
        $[9] = rawValuesByDate;
        $[10] = valuesByDate;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      t7 = t8;
      break bb0;
    }
    let rawByBucket;
    let t8;
    if ($[12] !== effectiveRule || $[13] !== fieldMap || $[14] !== granularity || $[15] !== rawValuesByDate || $[16] !== timezone || $[17] !== valuesByDate) {
      const source = Object.values(rawValuesByDate ?? valuesByDate).flat();
      rawByBucket = groupValuesByBucket(source, granularity, timezone);
      t8 = aggregateBucketValues(rawByBucket, fieldMap, effectiveRule);
      $[12] = effectiveRule;
      $[13] = fieldMap;
      $[14] = granularity;
      $[15] = rawValuesByDate;
      $[16] = timezone;
      $[17] = valuesByDate;
      $[18] = rawByBucket;
      $[19] = t8;
    } else {
      rawByBucket = $[18];
      t8 = $[19];
    }
    let t9;
    if ($[20] !== rawByBucket || $[21] !== t8) {
      t9 = {
        bucketedValues: t8,
        rawBucketedValues: rawByBucket
      };
      $[20] = rawByBucket;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    t7 = t9;
  }
  const {
    bucketedValues,
    rawBucketedValues
  } = t7;
  const noop = _temp11;
  const goalHandler = onOpenGoalDialog ?? noop;
  let t8;
  if ($[23] !== canRenderBody || $[24] !== granularity || $[25] !== hideChartOption || $[26] !== onDisplayTypeChange || $[27] !== renderDisplayType || $[28] !== rule || $[29] !== setGranularity || $[30] !== setRule) {
    t8 = onDisplayTypeChange && <div className="flex items-center gap-1 px-4 pt-2">{([...(canRenderBody ? [{
        type: "BODY" as const,
        icon: IoBodyOutline,
        label: "Body"
      }] : []), {
        type: "TABLE",
        icon: FiList,
        label: "Table"
      }, {
        type: "CARDS",
        icon: FiGrid,
        label: "Cards"
      }, ...(!hideChartOption ? [{
        type: "CHART" as const,
        icon: FiBarChart2,
        label: "Chart"
      }] : [])] as const).map(t9 => {
        const {
          type,
          icon: Icon,
          label
        } = t9;
        return <button key={type} type="button" onClick={() => onDisplayTypeChange(type)} className={cx("rounded-md p-1.5 transition-colors", renderDisplayType === type ? "bg-primary text-primary-foreground" : "text-foreground-muted hover:bg-hover hover:text-foreground-secondary")} title={label}><Icon size={14} /></button>;
      })}<div className="ml-auto"><DropdownMenu><DropdownMenuTrigger asChild={true}><Button variant="secondary" size="sm" className="gap-1.5 font-normal" data-testid="measurement-bucket-button"><span>{granularity === "NONE" ? MEASUREMENT_BUCKET_LABELS.NONE : `${MEASUREMENT_BUCKET_LABELS[granularity]} · ${MEASUREMENT_RULE_LABELS[rule]}`}</span><FiChevronDown size={13} className="shrink-0" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuLabel>Group by</DropdownMenuLabel><DropdownMenuRadioGroup value={granularity} onValueChange={value => setGranularity(value as MeasurementBucketGranularity)}>{(["NONE", "DAY", "WEEK"] as const).map(_temp12)}</DropdownMenuRadioGroup><DropdownMenuSeparator /><DropdownMenuLabel>Combine with</DropdownMenuLabel><DropdownMenuRadioGroup value={rule} onValueChange={value_0 => setRule(value_0 as MeasurementAggregationRule)}>{(["SUM", "AVERAGE", "LATEST"] as const).map(option_0 => <DropdownMenuRadioItem key={option_0} value={option_0} disabled={granularity === "NONE"}>{MEASUREMENT_RULE_LABELS[option_0]}</DropdownMenuRadioItem>)}</DropdownMenuRadioGroup></DropdownMenuContent></DropdownMenu></div></div>;
    $[23] = canRenderBody;
    $[24] = granularity;
    $[25] = hideChartOption;
    $[26] = onDisplayTypeChange;
    $[27] = renderDisplayType;
    $[28] = rule;
    $[29] = setGranularity;
    $[30] = setRule;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const t9 = onDisplayTypeChange || previewMode ? "p-4" : "";
  let t10;
  if ($[32] !== bucketedValues || $[33] !== fieldMap || $[34] !== goalHandler || $[35] !== granularity || $[36] !== group || $[37] !== latestGoals || $[38] !== patientId || $[39] !== previewMode || $[40] !== rawBucketedValues || $[41] !== renderDisplayType || $[42] !== timezone) {
    t10 = renderDisplayType === "TABLE" && <TableDisplay group={group} valuesByDate={bucketedValues} rawValuesByDate={rawBucketedValues} granularity={granularity} timeZone={timezone} fieldMap={fieldMap} patientId={patientId || ""} latestGoals={latestGoals} onOpenGoalDialog={goalHandler} previewMode={previewMode} />;
    $[32] = bucketedValues;
    $[33] = fieldMap;
    $[34] = goalHandler;
    $[35] = granularity;
    $[36] = group;
    $[37] = latestGoals;
    $[38] = patientId;
    $[39] = previewMode;
    $[40] = rawBucketedValues;
    $[41] = renderDisplayType;
    $[42] = timezone;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== bucketedValues || $[45] !== extraAnnotations || $[46] !== fieldMap || $[47] !== granularity || $[48] !== group || $[49] !== latestGoals || $[50] !== previewMode || $[51] !== renderDisplayType || $[52] !== timezone) {
    t11 = renderDisplayType === "CHART" && <ChartDisplay group={group} valuesByDate={bucketedValues} granularity={granularity} timeZone={timezone} fieldMap={fieldMap} latestGoals={latestGoals} extraAnnotations={extraAnnotations} previewMode={previewMode} />;
    $[44] = bucketedValues;
    $[45] = extraAnnotations;
    $[46] = fieldMap;
    $[47] = granularity;
    $[48] = group;
    $[49] = latestGoals;
    $[50] = previewMode;
    $[51] = renderDisplayType;
    $[52] = timezone;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== bucketedValues || $[55] !== fieldMap || $[56] !== goalHandler || $[57] !== granularity || $[58] !== group || $[59] !== latestGoals || $[60] !== patientId || $[61] !== previewMode || $[62] !== renderDisplayType || $[63] !== timezone) {
    t12 = renderDisplayType === "CARDS" && <CardsDisplay group={group} valuesByDate={bucketedValues} granularity={granularity} timeZone={timezone} fieldMap={fieldMap} patientId={patientId || ""} latestGoals={latestGoals} onOpenGoalDialog={goalHandler} previewMode={previewMode} />;
    $[54] = bucketedValues;
    $[55] = fieldMap;
    $[56] = goalHandler;
    $[57] = granularity;
    $[58] = group;
    $[59] = latestGoals;
    $[60] = patientId;
    $[61] = previewMode;
    $[62] = renderDisplayType;
    $[63] = timezone;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  let t13;
  if ($[65] !== bucketedValues || $[66] !== granularity || $[67] !== group || $[68] !== renderDisplayType || $[69] !== timezone) {
    t13 = renderDisplayType === "BODY" && <BodyDisplay group={group} valuesByDate={bucketedValues} granularity={granularity} timeZone={timezone} />;
    $[65] = bucketedValues;
    $[66] = granularity;
    $[67] = group;
    $[68] = renderDisplayType;
    $[69] = timezone;
    $[70] = t13;
  } else {
    t13 = $[70];
  }
  let t14;
  if ($[71] !== t10 || $[72] !== t11 || $[73] !== t12 || $[74] !== t13 || $[75] !== t9) {
    t14 = <div className={t9}>{t10}{t11}{t12}{t13}</div>;
    $[71] = t10;
    $[72] = t11;
    $[73] = t12;
    $[74] = t13;
    $[75] = t9;
    $[76] = t14;
  } else {
    t14 = $[76];
  }
  let t15;
  if ($[77] !== t14 || $[78] !== t8) {
    t15 = <div>{t8}{t14}</div>;
    $[77] = t14;
    $[78] = t8;
    $[79] = t15;
  } else {
    t15 = $[79];
  }
  return t15;
}

// === Main Component ===
function _temp12(option) {
  return <DropdownMenuRadioItem key={option} value={option}>{MEASUREMENT_BUCKET_LABELS[option]}</DropdownMenuRadioItem>;
}
function _temp11() {}
export default function MeasurementGroupCard(t0) {
  const $ = _c(68);
  const {
    group,
    valuesByDate,
    rawValuesByDate,
    patientId,
    onRecord,
    variant: t1,
    defaultDisplayType: defaultDisplayTypeProp,
    hideChartOption: t2
  } = t0;
  const variant = t1 === undefined ? "card" : t1;
  const hideChartOption = t2 === undefined ? false : t2;
  const [collapsed, setCollapsed] = useState(false);
  const [goalDialogState, setGoalDialogState] = useState(null);
  const bucketing = useMeasurementBucketing();
  const rawDisplayType = defaultDisplayTypeProp ?? group.displayConfig?.type ?? "TABLE";
  let t3;
  if ($[0] !== group) {
    t3 = canRenderBodyGroup(group);
    $[0] = group;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const canRenderBody = t3;
  let t4;
  if ($[2] !== canRenderBody || $[3] !== rawDisplayType) {
    t4 = resolveRenderableDisplayType(rawDisplayType, canRenderBody);
    $[2] = canRenderBody;
    $[3] = rawDisplayType;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const defaultDisplayType = t4;
  const [displayType, setDisplayType] = useState(defaultDisplayType);
  let t5;
  if ($[5] !== group) {
    t5 = buildFieldMap(group);
    $[5] = group;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const fieldMap = t5;
  let t6;
  if ($[7] !== valuesByDate) {
    t6 = latestGoalsFromValues(valuesByDate);
    $[7] = valuesByDate;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const latestGoals = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (field, goal) => {
      setGoalDialogState({
        field,
        goal: goal?.isComputed ? null : goal ?? null
      });
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleOpenGoalDialog = t7;
  let t8;
  if ($[10] !== group) {
    t8 = resolveGroupMemberships(group);
    $[10] = group;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const allResolvedItems = t8;
  let t9;
  if ($[12] !== allResolvedItems) {
    t9 = allResolvedItems.filter(_temp13);
    $[12] = allResolvedItems;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const numericFieldItems = t9;
  let t10;
  if ($[14] !== numericFieldItems) {
    t10 = numericFieldItems.map(_temp14);
    $[14] = numericFieldItems;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const trendFields = t10;
  let t11;
  if ($[16] !== valuesByDate) {
    t11 = Object.keys(valuesByDate);
    $[16] = valuesByDate;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const hasTrendData = t11.length >= 2;
  const Wrapper = variant === "card" ? Card : "div";
  const t12 = variant === "transparent" && "border-b border-border";
  let t13;
  if ($[18] !== t12) {
    t13 = cx("overflow-hidden p-0", t12);
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = `group-card-${group.id}`;
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setCollapsed(_temp15);
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== collapsed) {
    t16 = collapsed ? <FiChevronDown size={14} className="shrink-0 text-foreground-secondary" /> : <FiChevronUp size={14} className="shrink-0 text-foreground-secondary" />;
    $[21] = collapsed;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== group.color) {
    t17 = group.color && <span className="inline-block h-3 w-3 shrink-0 rounded-full" style={{
      backgroundColor: group.color
    }} />;
    $[23] = group.color;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== group.name) {
    t18 = <h4 className="text-body-emphasis text-foreground">{group.name}</h4>;
    $[25] = group.name;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t16 || $[28] !== t17 || $[29] !== t18) {
    t19 = <div className="flex items-center gap-2">{t16}{t17}{t18}</div>;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== group.description) {
    t20 = group.description && <p className="ml-[22px] mt-0.5 text-caption text-muted-foreground">{group.description}</p>;
    $[31] = group.description;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t19 || $[34] !== t20) {
    t21 = <div className="min-w-0 flex-1">{t19}{t20}</div>;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const t22 = `record-button-${group.id}`;
  let t23;
  if ($[36] !== onRecord) {
    t23 = e => {
      e.stopPropagation();
      onRecord();
    };
    $[36] = onRecord;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t22 || $[39] !== t23) {
    t24 = <Button variant="secondary" size="sm" icon={FiPlus} className="shrink-0" data-testid={t22} onClick={t23}>Record</Button>;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t21 || $[42] !== t24) {
    t25 = <div className="flex cursor-pointer items-center justify-between border-b border-border px-4 py-3 hover:bg-hover" onClick={t15}>{t21}{t24}</div>;
    $[41] = t21;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== bucketing || $[45] !== collapsed || $[46] !== displayType || $[47] !== fieldMap || $[48] !== group || $[49] !== hasTrendData || $[50] !== hideChartOption || $[51] !== latestGoals || $[52] !== numericFieldItems.length || $[53] !== patientId || $[54] !== rawValuesByDate || $[55] !== trendFields || $[56] !== valuesByDate) {
    t26 = !collapsed && <><MeasurementGroupCardContent group={group} valuesByDate={valuesByDate} rawValuesByDate={rawValuesByDate} displayType={displayType} patientId={patientId} onDisplayTypeChange={setDisplayType} onOpenGoalDialog={handleOpenGoalDialog} fieldMap={fieldMap} latestGoals={latestGoals} hideChartOption={hideChartOption} bucketing={bucketing} /><TrendChartsSection patientId={patientId} fields={trendFields} latestGoals={latestGoals} enabled={numericFieldItems.length > 0 && hasTrendData} granularity={bucketing.granularity} rule={bucketing.effectiveRule} /></>;
    $[44] = bucketing;
    $[45] = collapsed;
    $[46] = displayType;
    $[47] = fieldMap;
    $[48] = group;
    $[49] = hasTrendData;
    $[50] = hideChartOption;
    $[51] = latestGoals;
    $[52] = numericFieldItems.length;
    $[53] = patientId;
    $[54] = rawValuesByDate;
    $[55] = trendFields;
    $[56] = valuesByDate;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== goalDialogState || $[59] !== patientId) {
    t27 = goalDialogState && <MeasurementGoalDialog patientId={patientId} field={goalDialogState.field} existingGoal={goalDialogState.goal} open={!!goalDialogState} onOpenChange={open => {
      if (!open) {
        setGoalDialogState(null);
      }
    }} />;
    $[58] = goalDialogState;
    $[59] = patientId;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== Wrapper || $[62] !== t13 || $[63] !== t14 || $[64] !== t25 || $[65] !== t26 || $[66] !== t27) {
    t28 = <Wrapper className={t13} data-testid={t14}>{t25}{t26}{t27}</Wrapper>;
    $[61] = Wrapper;
    $[62] = t13;
    $[63] = t14;
    $[64] = t25;
    $[65] = t26;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  return t28;
}
function _temp15(prev) {
  return !prev;
}
function _temp14(item_0) {
  return {
    id: item_0.field.id,
    name: item_0.field.name,
    unit: item_0.field.unit,
    color: getFieldChartColor(item_0.field),
    referenceRanges: item_0.field.referenceRanges ?? undefined,
    dataType: item_0.field.dataType,
    decimalPlaces: item_0.field.decimalPlaces
  };
}
function _temp13(item) {
  return item.type === "field" && isNumericDataType(item.field.dataType);
}